import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, ShoppingCart } from 'lucide-react';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [cartItemCount, setCartItemCount] = useState(0);

  useEffect(() => {
    const handleModalOpen = () => setIsHidden(true);
    const handleModalClose = () => setIsHidden(false);
    
    window.addEventListener('modalOpen', handleModalOpen);
    window.addEventListener('modalClose', handleModalClose);
    
    return () => {
      window.removeEventListener('modalOpen', handleModalOpen);
      window.removeEventListener('modalClose', handleModalClose);
    };
  }, []);

  useEffect(() => {
    // Update cart count
    const updateCartCount = () => {
      const cart = JSON.parse(localStorage.getItem('cart') || '{}');
      const count = Object.values(cart).reduce((total: number, quantity) => total + (quantity as number), 0);
      setCartItemCount(count);
    };

    updateCartCount();
    
    // Listen for storage changes
    window.addEventListener('storage', updateCartCount);
    
    // Listen for custom cart update events
    window.addEventListener('cartUpdated', updateCartCount);
    
    // Listen for global cart updates
    window.addEventListener('globalCartUpdate', updateCartCount);
    
    return () => {
      window.removeEventListener('storage', updateCartCount);
      window.removeEventListener('cartUpdated', updateCartCount);
      window.removeEventListener('globalCartUpdate', updateCartCount);
    };
  }, []);

  const getAboutHref = () => {
    // Determine about link based on current section
    if (location.pathname.includes('/robotics')) {
      return '/about?section=robotics';
    } else if (location.pathname.includes('/unlimited')) {
      return '/about?section=unlimited';
    } else if (location.pathname.includes('/sex-machines')) {
      return '/about?section=sex-machines';
    } else if (location.pathname.includes('/services')) {
      // When on services page, use the last visited section from sessionStorage
      const lastSection = sessionStorage.getItem('currentSection');
      if (lastSection) {
        return `/about?section=${lastSection}`;
      }
    }
    return '/about';
  };

  const navigation = [
    { name: t('navigation.about'), href: getAboutHref() },
    { name: t('navigation.services'), href: '/services' },
    { name: t('navigation.shop'), href: '/shop' },
    { name: t('navigation.leasing'), href: '/operativni-leasing' },
    { name: t('navigation.news'), href: '/news' },
    { name: t('navigation.files'), href: '/soubory' },
    { name: t('navigation.contact'), href: '/contact' },
  ];

  const languages = [
    { code: 'cz', name: 'Čeština', flag: 'https://flagcdn.com/w20/cz.png' },
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w20/gb.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w20/de.png' },
    { code: 'sk', name: 'Slovenčina', flag: 'https://flagcdn.com/w20/sk.png' }
  ];

  const changeLanguage = (locale: string) => {
    i18n.changeLanguage(locale);
    // Force re-render of components that use currency formatting
    window.dispatchEvent(new Event('languageChanged'));
    setShowLanguages(false);
  };

  return (
    <motion.nav 
      initial={{ y: 0 }}
      animate={{ y: isHidden ? -100 : 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className="fixed top-4 left-4 right-4 z-50"
    >
      <div className="bg-black/20 backdrop-blur-md border border-[#EE1C23]/30 rounded-2xl px-6 py-3 max-w-fit mx-auto">
        <div className="flex items-center justify-between space-x-8">
          {/* Logo and Back Button */}
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-2 text-[#EE1C23] hover:text-red-300 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <img src="https://res.cloudinary.com/dvq5iv4qs/image/upload/v1760482265/-ct_logo_light_copy_cxvyoy.png" alt="Cybertronic Logo" className="h-8" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="flex items-center space-x-6">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => window.scrollTo(0, 0)}
                  className={`px-3 py-1 text-sm font-medium transition-colors rounded-lg ${
                    location.pathname === item.href
                      ? 'text-[#EE1C23] bg-[#EE1C23]/10'
                      : 'text-gray-300 hover:text-[#EE1C23] hover:bg-white/5'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Cart Icon */}
            <Link to="/cart" className="relative">
              <ShoppingCart className="w-5 h-5 text-gray-300 hover:text-[#EE1C23] transition-colors" />
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-[#EE1C23] text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Link>

            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setShowLanguages(!showLanguages)}
                className="flex items-center space-x-2 bg-black/60 backdrop-blur-md border border-[#EE1C23]/30 rounded-lg px-3 py-1 text-white hover:border-[#EE1C23]/50 transition-colors"
              >
                {(() => {
                  const normalizedLang = i18n.language.split('-')[0].toLowerCase();
                  const currentLang = languages.find(lang => lang.code === normalizedLang);
                  return (
                    <>
                      <img
                        src={currentLang?.flag || 'https://flagcdn.com/w20/cz.png'}
                        alt="Current language"
                        className="w-4 h-3"
                      />
                      <span className="text-sm font-medium">{currentLang?.code === 'cz' ? 'CZ' : currentLang?.code.toUpperCase()}</span>
                    </>
                  );
                })()}
              </button>

              {showLanguages && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute right-0 mt-1 bg-black/80 backdrop-blur-md border border-[#EE1C23]/30 rounded-lg shadow-xl overflow-hidden"
                >
                  {(() => {
                    const normalizedLang = i18n.language.split('-')[0].toLowerCase();
                    return languages
                      .filter(lang => lang.code !== normalizedLang)
                      .map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className="flex items-center w-full px-3 py-2 text-left text-white hover:bg-white/10 transition-colors whitespace-nowrap"
                        >
                          <img src={lang.flag} alt={lang.name} className="w-4 h-3" />
                          <span className="text-sm font-medium ml-2">{lang.code === 'cz' ? 'CZ' : lang.code.toUpperCase()}</span>
                        </button>
                      ));
                  })()}
                </motion.div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex flex-col justify-center items-center w-8 h-8 space-y-1.5 focus:outline-none"
                aria-label="Toggle menu"
              >
                <span className={`block w-6 h-0.5 bg-gray-300 transition-all duration-300 ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                <span className={`block w-6 h-0.5 bg-gray-300 transition-all duration-300 ${isOpen ? 'opacity-0' : ''}`}></span>
                <span className={`block w-6 h-0.5 bg-gray-300 transition-all duration-300 ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.2 }}
          className="md:hidden bg-black/40 backdrop-blur-md rounded-2xl mt-2 mx-4"
        >
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 text-base font-medium transition-colors rounded-lg ${
                  location.pathname === item.href
                    ? 'text-[#EE1C23] bg-[#EE1C23]/10'
                    : 'text-gray-300 hover:text-[#EE1C23] hover:bg-gray-800/50'
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </motion.nav>
  );
}